import { useState } from 'react';
import type { SummaryResponse } from '../api.js';
import type { Translate } from '../i18n/index.js';
import { theme } from '../theme.js';
import { Card, PageTitle } from '../components/ui.js';

/**
 * What a company sold and bought over a period it chooses.
 *
 * The period is the whole point. "This month" is the question a business asks
 * most often and it has to start clean on the first, but the same screen has to
 * answer "this year" and "that fortnight in Ramadan" without becoming a
 * different screen — so the presets set the range and the range is what the
 * figures come from. There is no separate monthly mode to fall out of step.
 */

export interface DateRange {
  from: string;
  to: string;
}

const iso = (d: Date) => d.toISOString().slice(0, 10);

/** The periods worth one click. Each is computed fresh, so none goes stale. */
export function presets(today = new Date()): { key: string; label: string; range: DateRange }[] {
  const y = today.getUTCFullYear();
  const m = today.getUTCMonth();

  const startOfMonth = new Date(Date.UTC(y, m, 1));
  const startOfLastMonth = new Date(Date.UTC(y, m - 1, 1));
  const endOfLastMonth = new Date(Date.UTC(y, m, 0));
  const startOfYear = new Date(Date.UTC(y, 0, 1));

  return [
    { key: 'month', label: 'This month', range: { from: iso(startOfMonth), to: iso(today) } },
    {
      key: 'last',
      label: 'Last month',
      range: { from: iso(startOfLastMonth), to: iso(endOfLastMonth) },
    },
    { key: 'year', label: 'This year', range: { from: iso(startOfYear), to: iso(today) } },
  ];
}

function money(value: number, currency: string): string {
  return `${value.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
}

function monthName(month: string, t: Translate): string {
  const [year, m] = month.split('-');
  const names = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  return `${t(names[Number(m) - 1] ?? month)} ${year}`;
}

export function SummaryScreen({
  range,
  data,
  loading,
  error,
  t,
  onRange,
}: {
  range: DateRange;
  data: SummaryResponse | null;
  loading: boolean;
  error: string | null;
  t: Translate;
  onRange: (range: DateRange) => void;
}) {
  const [draft, setDraft] = useState<DateRange>(range);
  const currency = data?.currency ?? '';

  const inputStyle = {
    height: '34px',
    border: `1px solid ${theme.borderStrong}`,
    borderRadius: theme.radiusControl,
    padding: '0 9px',
    fontSize: '13px',
    background: '#fff',
  };

  const biggestMonth = Math.max(
    1,
    ...(data?.months ?? []).map((m) => Math.max(m.sales, m.purchases)),
  );

  return (
    <div style={{ padding: '22px 24px 40px', maxWidth: '1100px' }}>
      <PageTitle
        title={t('Sales and purchases')}
        sub={t('Taken from Xero. Drafts, voided and deleted documents are not counted.')}
      />
      <div style={{ height: '16px' }} />

      <Card style={{ marginBottom: '16px' }}>
        <div style={{ padding: '13px 16px', display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'flex-end' }}>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {presets().map((preset) => {
              const on = preset.range.from === range.from && preset.range.to === range.to;
              return (
                <button
                  key={preset.key}
                  type="button"
                  onClick={() => {
                    setDraft(preset.range);
                    onRange(preset.range);
                  }}
                  style={{
                    border: `1px solid ${on ? theme.nav : theme.borderStrong}`,
                    background: on ? theme.nav : 'transparent',
                    color: on ? '#fff' : theme.text,
                    borderRadius: theme.radiusPill,
                    padding: '7px 14px',
                    fontSize: '12.5px',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  {t(preset.label)}
                </button>
              );
            })}
          </div>

          <div style={{ flex: 1 }} />

          <label style={{ fontSize: '12px', fontWeight: 600 }}>
            <span style={{ display: 'block', marginBottom: '4px' }}>{t('From')}</span>
            <input
              type="date"
              value={draft.from}
              onChange={(e) => setDraft({ ...draft, from: e.target.value })}
              style={inputStyle}
            />
          </label>
          <label style={{ fontSize: '12px', fontWeight: 600 }}>
            <span style={{ display: 'block', marginBottom: '4px' }}>{t('To')}</span>
            <input
              type="date"
              value={draft.to}
              onChange={(e) => setDraft({ ...draft, to: e.target.value })}
              style={inputStyle}
            />
          </label>
          <button
            type="button"
            onClick={() => onRange(draft)}
            style={{
              height: '34px',
              background: theme.nav,
              color: '#fff',
              border: 0,
              borderRadius: theme.radiusControl,
              padding: '0 16px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {t('Show')}
          </button>
        </div>
      </Card>

      {error && (
        <Card style={{ marginBottom: '16px' }}>
          <div style={{ padding: '14px 16px', color: theme.badDeep, fontSize: '13px', fontWeight: 600 }}>
            {t(error)}
          </div>
        </Card>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: '12px', marginBottom: '16px' }}>
        <Figure
          label={t('Sales')}
          value={loading ? '…' : money(data?.sales.total ?? 0, currency)}
          sub={`${data?.sales.count ?? 0} ${t('documents')}`}
          colour={theme.goodDeep}
        />
        <Figure
          label={t('Purchases')}
          value={loading ? '…' : money(data?.purchases.total ?? 0, currency)}
          sub={`${data?.purchases.count ?? 0} ${t('documents')}`}
          colour={theme.text}
        />
        <Figure
          label={t('Difference')}
          value={loading ? '…' : money((data?.sales.total ?? 0) - (data?.purchases.total ?? 0), currency)}
          sub={t('Sales less purchases')}
          colour={
            (data?.sales.total ?? 0) - (data?.purchases.total ?? 0) < 0 ? theme.badDeep : theme.goodDeep
          }
        />
      </div>

      {data && (data.excluded.drafts > 0 || data.excluded.voided > 0) && (
        <div style={{ fontSize: '12px', color: theme.textMuted, marginBottom: '16px', lineHeight: 1.6 }}>
          {t('Not counted:')} {data.excluded.drafts} {t('drafts')}
          {data.excluded.voided > 0 && `, ${data.excluded.voided} ${t('voided or deleted')}`}.{' '}
          {t('A draft is not a sale until somebody approves it.')}
        </div>
      )}

      <Card style={{ marginBottom: '16px' }}>
        <div style={{ padding: '12px 16px', borderBottom: `1px solid ${theme.dividerSoft}`, fontWeight: 600, fontSize: '13px', background: theme.surfaceHeader }}>
          {t('Month by month')}
        </div>
        {(data?.months.length ?? 0) === 0 ? (
          <div style={{ padding: '18px 16px', color: theme.textMuted, fontSize: '13px' }}>
            {loading ? t('Reading from Xero…') : t('Nothing in this period.')}
          </div>
        ) : (
          data!.months.map((month) => (
            <div key={month.month} style={{ padding: '10px 16px', borderBottom: `1px solid ${theme.divider}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12.5px', marginBottom: '5px' }}>
                <span style={{ fontWeight: 600 }}>{monthName(month.month, t)}</span>
                <span style={{ fontFamily: theme.fontMono }}>
                  {money(month.sales, currency)}
                </span>
              </div>
              <Bar value={month.sales} max={biggestMonth} colour={theme.good} />
              <div style={{ height: '3px' }} />
              <Bar value={month.purchases} max={biggestMonth} colour={theme.textSubtle} />
            </div>
          ))
        )}
      </Card>

      <Card>
        <div style={{ padding: '12px 16px', borderBottom: `1px solid ${theme.dividerSoft}`, fontWeight: 600, fontSize: '13px', background: theme.surfaceHeader }}>
          {t('Best selling')}
          <span style={{ fontWeight: 400, color: theme.textMuted, marginInlineStart: '8px', fontSize: '12px' }}>
            {t('by value sold')}
          </span>
        </div>
        {(data?.topItems.length ?? 0) === 0 ? (
          <div style={{ padding: '18px 16px', color: theme.textMuted, fontSize: '13px' }}>
            {loading ? t('Reading from Xero…') : t('Nothing sold in this period.')}
          </div>
        ) : (
          data!.topItems.map((item) => (
            <div
              key={`${item.code}-${item.name}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '10px 16px',
                borderBottom: `1px solid ${theme.divider}`,
              }}
            >
              <div style={{ flex: '0 0 92px', fontFamily: theme.fontMono, fontSize: '12px', color: theme.textMuted }} dir="ltr">
                {item.code}
              </div>
              <div style={{ flex: 1, minWidth: 0, fontSize: '13px' }}>{item.name}</div>
              <div style={{ flex: '0 0 90px', textAlign: 'end', fontSize: '12.5px', color: theme.textMuted }}>
                {item.quantity.toLocaleString('en-GB')}
              </div>
              <div style={{ flex: '0 0 130px', textAlign: 'end', fontFamily: theme.fontMono, fontSize: '12.5px' }}>
                {money(item.amount, currency)}
              </div>
            </div>
          ))
        )}
      </Card>

      {data?.truncated && (
        <div style={{ marginTop: '12px', fontSize: '12px', color: theme.warnDeep }}>
          {t('This period holds more documents than can be read at once. The figures cover only part of it — choose a shorter period.')}
        </div>
      )}
    </div>
  );
}

function Figure({ label, value, sub, colour }: { label: string; value: string; sub: string; colour: string }) {
  return (
    <Card>
      <div style={{ padding: '14px 16px' }}>
        <div style={{ fontSize: '12px', color: theme.textMuted, fontWeight: 600 }}>{label}</div>
        <div style={{ fontSize: '22px', fontWeight: 700, color: colour, margin: '4px 0 2px', fontFamily: theme.fontMono }}>
          {value}
        </div>
        <div style={{ fontSize: '11.5px', color: theme.textMuted }}>{sub}</div>
      </div>
    </Card>
  );
}

function Bar({ value, max, colour }: { value: number; max: number; colour: string }) {
  return (
    <div style={{ height: '7px', background: theme.surfaceSunken, borderRadius: '4px', overflow: 'hidden' }}>
      <div style={{ width: `${Math.min(100, (value / max) * 100)}%`, height: '100%', background: colour }} />
    </div>
  );
}
